import {
  Box,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Outer wrapper that aligns all filter controls in a row
 */
export const StyledFiltersContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  justifyContent: 'space-between',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

/**
 * Segmented toggle group — pill-shaped button group mimicking Figma design.
 * Uses info (blue) border color from the design token.
 */
export const StyledToggleButtonGroup = styled(ToggleButtonGroup)(
  ({ theme }) => ({
    height: theme.spacing(9),
    '& .MuiToggleButton-root': {
      height: theme.spacing(9),
      border: `1px solid ${theme.palette.info.light}`,
      color: theme.palette.text.secondary,
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.08px',
      textTransform: 'none',
      padding: theme.spacing(0, 4),
      transition: theme.transitions.create(
        ['background-color', 'color', 'border-color'],
        { duration: theme.transitions.duration.short },
      ),

      '&:not(:first-of-type)': {
        marginLeft: '-1px',
        borderLeft: `1px solid ${theme.palette.info.light}`,
      },

      '&:first-of-type': {
        borderRadius: `${theme.customShape.borderRadiusFull}px ${0}px ${0}px ${theme.customShape.borderRadiusFull}px`,
      },

      '&:last-of-type': {
        borderRadius: `${0}px ${theme.customShape.borderRadiusFull}px ${theme.customShape.borderRadiusFull}px ${0}px`,
      },

      '&:only-of-type': {
        borderRadius: `${theme.customShape.borderRadiusFull}px`,
      },

      '&.Mui-selected': {
        backgroundColor: theme.palette.info.main,
        color: theme.palette.common.white,
        borderColor: theme.palette.info.main,
        '&:hover': {
          backgroundColor: theme.palette.info.dark,
        },
      },

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },

    [theme.breakpoints.down('md')]: {
      width: '100%',
      '& .MuiToggleButton-root': {
        flex: 1,
      },
    },
  }),
);

/**
 * Individual toggle button — no extra styles needed, controlled by group.
 */
export const StyledToggleButton = styled(ToggleButton)(() => ({}));

export const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    alignItems: 'stretch',
    '& > *': {
      flex: 1,
      minWidth: 0,
    },
  },
}));

/**
 * Pill-shaped dropdown select control, matching the Figma design.
 */
export const StyledSelect = styled(Select)(({ theme }) => ({
  height: theme.spacing(9),
  minWidth: theme.spacing(62),
  borderRadius: `${theme.customShape.borderRadiusFull}px`,

  [theme.breakpoints.down('md')]: {
    minWidth: 0,
    width: '100%',
  },
  fontSize: theme.typography.pxToRem(16),
  color: theme.palette.text.secondary,
  border:
    theme.palette.mode === 'dark'
      ? `1px solid ${theme.palette.text.fourth}`
      : `1px solid ${theme.palette.text.secondary}`,

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
    borderRadius: `${theme.customShape.borderRadiusFull}px`,
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.text.disabled,
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: '1px',
  },

  '& .MuiSelect-select': {
    padding: theme.spacing(0, 4),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    height: `${theme.spacing(9)} !important`,
  },

  '& .MuiSelect-icon': {
    right: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

/**
 * Label text inside select trigger
 */
export const StyledSelectLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 400,
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
  letterSpacing: '0.08px',
}));

/**
 * Menu item inside the select dropdown
 */
export const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  color: theme.palette.text.primary,
  padding: theme.spacing(1.5, 4),
}));
